import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';

export default function ReportsPage() {
  const [report, setReport] = useState(null);
  const [cars, setCars] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [period, setPeriod] = useState({ start: '', end: '' });
  const [selectedCar, setSelectedCar] = useState('all');
  const [selectedExpenseType, setSelectedExpenseType] = useState('all');
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const token = localStorage.getItem('token');
  const headers = { headers: { Authorization: `Bearer ${token}` } };

  const formatMoney = (value) =>
    new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);

  const toId = (val) => {
    if (!val && val !== 0) return String(val);
    try {
      if (typeof val === 'object') {
        if (val.$oid) return String(val.$oid);
        if (val._id && typeof val._id === 'object' && val._id.$oid)
          return String(val._id.$oid);
        if (val._id) return String(val._id);
        return JSON.stringify(val);
      }
      return String(val);
    } catch {
      return String(val);
    }
  };

  const toLocalIso = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getMonthRange = (date = new Date()) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    return { start: toLocalIso(first), end: toLocalIso(last) };
  };

  async function load() {
    setLoading(true);
    const params = new URLSearchParams();
    if (period.start) params.set('start', period.start);
    if (period.end) params.set('end', period.end);
    if (selectedCar !== 'all') params.set('carId', selectedCar);

    try {
      const [reportRes, carsRes, expensesRes] = await Promise.all([
        axios.get(`${API_URL}/api/reports/financial?${params.toString()}`, headers),
        axios.get(`${API_URL}/api/cars`, headers),
        axios.get(`${API_URL}/api/expenses?${params.toString()}`, headers),
      ]);

      setReport(reportRes.data);
      setCars(carsRes.data);
      setExpenses(expensesRes.data);

      const types = [...new Set(expensesRes.data.map((e) => e.type))].sort();
      setExpenseTypes(types);
    } catch (err) {
      console.error('Ошибка при загрузке отчёта:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const { start, end } = getMonthRange();
    setPeriod({ start, end });
  }, []);

  useEffect(() => {
    if (period.start && period.end) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, selectedCar]);

  const handleFilter = () => load();

  const filteredExpensesByType = useMemo(() => {
    if (selectedExpenseType === 'all') return expenses;
    return expenses.filter((e) => e.type === selectedExpenseType);
  }, [expenses, selectedExpenseType]);

  const expensesByTypeStats = useMemo(() => {
    const grouped = {};
    const relevantExpenses =
      selectedCar === 'all'
        ? expenses
        : expenses.filter((e) => toId(e.carId?._id || e.carId) === selectedCar);

    relevantExpenses.forEach((expense) => {
      if (!grouped[expense.type]) {
        grouped[expense.type] = {
          type: expense.type,
          total: 0,
          count: 0,
        };
      }
      grouped[expense.type].total += expense.amount;
      grouped[expense.type].count += 1;
    });

    return Object.values(grouped).sort((a, b) => b.total - a.total);
  }, [expenses, selectedCar]);

  const getSummary = () => {
    if (!report) return { income: 0, expense: 0, profit: 0 };

    if (selectedCar === 'all') {
      return {
        income: report.totalRevenue,
        expense: report.totalExpenses,
        profit: report.profit,
      };
    } else {
      const carIncome =
        report.revenueByCar?.find((r) => toId(r._id) === selectedCar)?.revenue || 0;
      const carExpense =
        report.expensesByCar?.find((r) => toId(r._id) === selectedCar)?.expenses || 0;

      return {
        income: carIncome,
        expense: carExpense,
        profit: carIncome - carExpense,
      };
    }
  };

  const summary = getSummary();

  return (
    <div className="space-y-6">
      {/* Основные показатели */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ReportCard
          title="Доход"
          value={formatMoney(summary.income)}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          }
          color="text-emerald-400"
          loading={loading}
        />

        <ReportCard
          title="Затраты"
          value={formatMoney(summary.expense)}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"
              />
            </svg>
          }
          color="text-red-400"
          loading={loading}
          onClick={() => setShowExpenseModal(true)}
          clickable
        />

        <ReportCard
          title="Выгода"
          value={formatMoney(summary.profit)}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          }
          color={summary.profit >= 0 ? 'text-emerald-400' : 'text-red-400'}
          loading={loading}
        />
      </div>

      {/* Фильтры */}
      <div className="bg-zinc-900 p-4 md:p-6 rounded-xl border border-zinc-800">
        <h3 className="text-sm font-medium text-zinc-400 mb-4">Фильтры отчета</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs text-zinc-400 mb-2">Начало периода</label>
            <input
              type="date"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              value={period.start}
              onChange={(e) => setPeriod((p) => ({ ...p, start: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-xs text-zinc-400 mb-2">Конец периода</label>
            <input
              type="date"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              value={period.end}
              onChange={(e) => setPeriod((p) => ({ ...p, end: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-xs text-zinc-400 mb-2">Машина</label>
            <select
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              value={selectedCar}
              onChange={(e) => setSelectedCar(e.target.value)}
            >
              <option value="all">Все машины</option>
              {cars.map((c) => (
                <option key={c._id} value={toId(c._id)}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={handleFilter}
              disabled={loading}
              className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-700 disabled:cursor-not-allowed px-4 py-2 rounded-lg transition-colors font-medium text-sm flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Загрузка...
                </>
              ) : (
                'Применить'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Таблицы доходов и расходов */}
      {report && selectedCar === 'all' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Доход по машинам */}
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden">
            <div className="bg-zinc-800/50 px-4 py-3 border-b border-zinc-800">
              <h3 className="font-medium text-zinc-200 flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-emerald-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
                Доход по машинам
              </h3>
            </div>
            <div className="p-4 max-h-96 overflow-y-auto">
              {report.revenueByCar && report.revenueByCar.length > 0 ? (
                <ul className="divide-y divide-zinc-800">
                  {report.revenueByCar.map((r) => {
                    const rid = toId(r._id);
                    const car = cars.find((c) => toId(c._id) === rid);
                    return (
                      <li
                        key={rid}
                        className="flex justify-between items-center py-3 hover:bg-zinc-800/50 px-2 rounded transition-colors"
                      >
                        <span className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                          <span className="text-sm">{car ? car.name : `Unknown (${rid})`}</span>
                        </span>
                        <span className="text-emerald-400 font-semibold">
                          {formatMoney(r.revenue)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="text-center py-8 text-zinc-500">
                  <svg
                    className="w-12 h-12 mx-auto mb-2 opacity-50"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                    />
                  </svg>
                  Нет данных за выбранный период
                </div>
              )}
            </div>
          </div>

          {/* Затраты по машинам */}
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden">
            <div className="bg-zinc-800/50 px-4 py-3 border-b border-zinc-800">
              <h3 className="font-medium text-zinc-200 flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"
                  />
                </svg>
                Затраты по машинам
              </h3>
            </div>
            <div className="p-4 max-h-96 overflow-y-auto">
              {report.expensesByCar && report.expensesByCar.length > 0 ? (
                <ul className="divide-y divide-zinc-800">
                  {report.expensesByCar.map((r) => {
                    const rid = toId(r._id);
                    const car = cars.find((c) => toId(c._id) === rid);

                    return (
                      <li
                        key={rid}
                        className="flex justify-between items-center py-3 hover:bg-zinc-800/50 px-2 rounded transition-colors"
                      >
                        <span className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                          <span className="text-sm">{car ? car.name : `Unknown (${rid})`}</span>
                        </span>
                        <span className="text-red-400 font-semibold">
                          {formatMoney(r.expenses)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="text-center py-8 text-zinc-500">
                  <svg
                    className="w-12 h-12 mx-auto mb-2 opacity-50"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                    />
                  </svg>
                  Нет данных за выбранный период
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно детализации затрат */}
      {showExpenseModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 rounded-2xl md:rounded-3xl w-full md:w-auto md:min-w-[90vw] md:max-w-7xl shadow-2xl border border-zinc-700/50 relative overflow-hidden max-h-[90vh] flex flex-col">
            {/* Декоративный фон (только десктоп) */}
            <div className="hidden md:block absolute top-0 right-0 w-64 h-64 bg-red-500/5 rounded-full blur-3xl"></div>
            <div className="hidden md:block absolute bottom-0 left-0 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl"></div>

            {/* Кнопка закрытия */}
            <button
              onClick={() => {
                setShowExpenseModal(false);
                setSelectedExpenseType('all');
              }}
              className="absolute top-3 right-3 md:top-4 md:right-4 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-full p-2 transition-all duration-200 z-10"
            >
              <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Заголовок */}
            <div className="relative p-4 md:p-6 border-b border-zinc-800 flex-shrink-0">
              <h2 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-red-400 to-red-600 bg-clip-text text-transparent">
                Детализация затрат
              </h2>
              <p className="text-xs md:text-sm text-zinc-500 mt-1">
                Общая сумма: <span className="text-red-400 font-semibold">{formatMoney(summary.expense)}</span>
              </p>
            </div>

            {/* Контент с прокруткой */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-4 md:p-6">
                {/* Горизонтальная сетка на десктопе */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
                  {/* Левая колонка - Фильтр */}
                  <div className="lg:col-span-1 space-y-4">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Фильтр по типу расхода</label>
                      <select
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 md:px-4 py-2 md:py-3 text-sm md:text-base focus:ring-2 focus:ring-red-500 focus:outline-none"
                        value={selectedExpenseType}
                        onChange={(e) => setSelectedExpenseType(e.target.value)}
                      >
                        <option value="all">Все типы</option>
                        {expenseTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Статистика по типам */}
                    {expensesByTypeStats.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-zinc-400 mb-3 flex items-center gap-2">
                          <svg
                            className="w-4 h-4 md:w-5 md:h-5 text-purple-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                            />
                          </svg>
                          Статистика по типам
                        </h3>
                        <div className="grid grid-cols-2 lg:grid-cols-1 gap-2 md:gap-3">
                          {expensesByTypeStats.map((item) => (
                            <div
                              key={item.type}
                              onClick={() => setSelectedExpenseType(item.type)}
                              className={`bg-zinc-800/50 rounded-lg p-2 md:p-3 border cursor-pointer transition-all ${
                                selectedExpenseType === item.type
                                  ? 'border-purple-500 bg-purple-500/10 ring-2 ring-purple-500/20'
                                  : 'border-zinc-700/50 hover:border-purple-500/30'
                              }`}
                            >
                              <div className="text-xs text-zinc-500 mb-1 truncate" title={item.type}>
                                {item.type}
                              </div>
                              <div className="text-base md:text-lg font-bold text-purple-400">
                                {formatMoney(item.total)}
                              </div>
                              <div className="text-xs text-zinc-600 mt-1">{item.count} операций</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Правая колонка - Таблица расходов */}
                  <div className="lg:col-span-2">
                    {filteredExpensesByType.length > 0 ? (
                      <div>
                        <h3 className="text-sm font-medium text-zinc-400 mb-3 flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <svg
                              className="w-4 h-4 md:w-5 md:h-5 text-red-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                              />
                            </svg>
                            {selectedExpenseType !== 'all' ? `Расходы: ${selectedExpenseType}` : 'Все расходы'}
                          </span>
                          <span className="text-xs text-zinc-500">{filteredExpensesByType.length} операций</span>
                        </h3>

                        {/* Таблица для десктопа */}
                        <div className="hidden md:block bg-zinc-800/30 rounded-xl overflow-hidden border border-zinc-700/50">
                          <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
                            <table className="w-full">
                              <thead className="bg-zinc-800/50 sticky top-0">
                                <tr className="text-xs text-zinc-500">
                                  <th className="text-left p-3">Дата</th>
                                  <th className="text-left p-3">Машина</th>
                                  <th className="text-left p-3">Тип</th>
                                  <th className="text-right p-3">Сумма</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-zinc-800">
                                {filteredExpensesByType.map((expense) => (
                                  <tr key={expense._id} className="hover:bg-zinc-800/30 transition-colors">
                                    <td className="p-3 text-sm text-zinc-400">
                                      {new Date(expense.date).toLocaleDateString('ru-RU', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                      })}
                                    </td>
                                    <td className="p-3 text-sm">{expense.carId?.name || 'Не указана'}</td>
                                    <td className="p-3">
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-500/10 text-purple-400 border border-purple-500/20">
                                        {expense.type}
                                      </span>
                                    </td>
                                    <td className="p-3 text-sm text-right text-red-400 font-semibold">
                                      {formatMoney(expense.amount)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Карточки для мобильных */}
                        <div className="grid md:hidden gap-3">
                          {filteredExpensesByType.map((expense) => (
                            <div
                              key={expense._id}
                              className="bg-zinc-800/50 rounded-lg p-3 border border-zinc-700/50"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-zinc-500">
                                  {new Date(expense.date).toLocaleDateString('ru-RU', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                  })}
                                </span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-purple-500/10 text-purple-400 border border-purple-500/20">
                                  {expense.type}
                                </span>
                              </div>
                              <div className="text-sm text-white mb-2">{expense.carId?.name || 'Не указана'}</div>
                              <div className="text-lg font-bold text-red-400">
                                {formatMoney(expense.amount)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-zinc-500">
                        <svg
                          className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 opacity-50"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                          />
                        </svg>
                        <p className="text-sm md:text-base">Нет данных за выбранный период</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReportCard({ title, value, color = 'text-white', icon, loading, onClick, clickable }) {
  return (
    <div
      onClick={onClick}
      className={`bg-zinc-900 rounded-xl border border-zinc-800 p-4 md:p-6 transition-all ${
        clickable
          ? 'cursor-pointer hover:border-zinc-700 hover:shadow-lg hover:shadow-red-500/10 active:scale-95 md:hover:scale-105'
          : 'hover:border-zinc-700'
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-zinc-400">{title}</div>
        <div className={color}>{icon}</div>
      </div>
      {loading ? (
        <div className="h-8 md:h-9 bg-zinc-800 animate-pulse rounded"></div>
      ) : (
        <div className={`text-xl md:text-3xl font-bold ${color}`}>{value}</div>
      )}
      {clickable && (
        <div className="mt-2 text-xs text-zinc-500 flex items-center gap-1">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
          Подробнее
        </div>
      )}
    </div>
  );
}